import type { EventDto } from '../../events/response/event.dto';
import type { FlowDto, FlowSlotDto } from '../../flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import type { EventSlotDto } from '../../events/response/event.dto';
import type { UserContributorDto } from './user.contributor.dto';
import type { RemoteSpeakerDayDto } from '@/types/dtos/contributors/request/configure-remote-speaker.dto';

export type UserReferenceDto = {
  id: string;
};

/** Event-level crew roles (dashboard + single-flow flow settings). */
export const EVENT_CREW_ROLES = ['owner', 'event_manager'] as const;
const EVENT_ACTOR_ROLES = [] as const;
export const EVENT_CONTRIBUTORS_ROLES = [...EVENT_ACTOR_ROLES, ...EVENT_CREW_ROLES] as const;
export type EventContributorRole = (typeof EVENT_CONTRIBUTORS_ROLES)[number];

/** Flow-level crew roles (non-host). Extend when the API adds roles; keep in sync with FLOW_CONTRIBUTORS_ROLES. */
export const FLOW_CREW_ROLES = [] as const;
const FLOW_ACTOR_ROLES = ['moderator'] as const;
export const FLOW_CONTRIBUTORS_ROLES = [...FLOW_ACTOR_ROLES, ...FLOW_CREW_ROLES] as const;
export type FlowContributorRole = (typeof FLOW_CONTRIBUTORS_ROLES)[number];

export const SESSION_CREW_ROLES = [] as const;
export const SESSION_ACTOR_ROLES = ['speaker', 'panelist', 'panel_host'] as const;
export const SESSION_CONTRIBUTORS_ROLES = [...SESSION_ACTOR_ROLES, ...SESSION_CREW_ROLES] as const;
export type SessionContributorRole = (typeof SESSION_CONTRIBUTORS_ROLES)[number];

// across all entities {{{
export const CREW_ROLES = [...EVENT_CREW_ROLES, ...FLOW_CREW_ROLES, ...SESSION_CREW_ROLES] as const;
export type CrewRole = (typeof CREW_ROLES)[number];

export const ACTOR_ROLES = [...EVENT_ACTOR_ROLES, ...FLOW_ACTOR_ROLES, ...SESSION_ACTOR_ROLES] as const;
export type ActorRole = (typeof ACTOR_ROLES)[number];
// }}}

export const CONTRIBUTORS_ROLES = [
  ...EVENT_CONTRIBUTORS_ROLES,
  ...FLOW_CONTRIBUTORS_ROLES,
  ...SESSION_CONTRIBUTORS_ROLES,
] as const;
export type ContributorRole = EventContributorRole | FlowContributorRole | SessionContributorRole;

export const CONTRIBUTOR_ROLE_SLOT_ENTITIES = {
  FLOW: 'Flow',
  FLOW_SLOT: 'FlowSlot',
  EVENT_SLOT: 'EventSlot',
} as const;
export type ContributorRoleSlotEntity =
  (typeof CONTRIBUTOR_ROLE_SLOT_ENTITIES)[keyof typeof CONTRIBUTOR_ROLE_SLOT_ENTITIES];

export type RoleDto<T extends ContributorRole = EventContributorRole> = {
  id: string;
  assignedAt: string;
  assignedBy: string;
  entityType: string;
  entity?: EventDto | FlowDto | SessionDto | string;
  role: T;
  slots: RoleSlotDto[];
  isMediaRequested: boolean;
  isRemoteSpeaker: boolean;
  effectiveRemoteSpeaker?: boolean;
  remoteSpeakerBlockReason?: string;
  isVisible: boolean;
  autoCreateSlots?: boolean;
  invitedBy: string;
  invitedAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  stagedForInvite?: boolean;
};

export type ContributorDto<T extends ContributorRole = ContributorRole> = {
  id: string;
  phoneNumber?: string;
  email?: string;
  /** Present when API exposes invite pipeline state separately from `email`. */
  invitedEmail?: string;
  invitedAt?: string;
  acceptedAt?: string;
  /** Shared organizer outbox: marked for bulk invite */
  stagedForInvite?: boolean;
  /** Whether the contributor email has opted out of emails from the event organization. */
  isOptedOut?: boolean;
  remoteSpeakerEnabled?: boolean;
  remoteSpeakerDefaultForNewRoles?: boolean;
  remoteSpeakerDays?: RemoteSpeakerDayDto[];
  profiles: ProfileDto[];
  roles: RoleDto<T>[];
  user?: UserReferenceDto;
  event?: EventDto;
  userContributor?: UserContributorDto;
};

export type RoleSlotDto = {
  id: string;
  entityType: ContributorRoleSlotEntity;
  entity: { id: string } | string | FlowDto | FlowSlotDto | EventSlotDto; // Can be object with id or string ID
  eventSlotId?: string;
  invitedBy?: string;
  invitedAt?: string;
  acceptedAt?: string;
  deleted?: boolean;
  isVisible?: boolean;
  isRemoteSpeaker?: boolean;
  remoteSpeakerAcceptedAt?: string;
  effectiveRemoteSpeaker?: boolean;
  remoteSpeakerBlockReason?: string;
  stagedForInvite?: boolean;
};

export type ProfileDto = {
  bio: string;
  displayName: string;
  editor: string;
  final: boolean;
  id: string;
  organization: string;
  assetName?: string;
  title: string;
  version: number;
  /** When present (API), compared to user-provided profile submission for review UI. */
  updatedAt?: string;
  /** The submittedVersion of the provided profile that the crew has reviewed. */
  reviewedSubmittedVersion?: number;
};
